import { QueryClientProvider } from '@tanstack/react-query';
import { useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useParams } from 'react-router-dom';

import { Toaster } from '@terra/ui';

import { AdminShell } from './components/AdminShell';
import { queryClient } from './lib/queryClient';
import { AccountPage } from './pages/AccountPage';
import {
  AgentPayoutTimePage,
  AgentRegistrationPage,
  AllAgentsPage,
  CommercePartnersPage,
} from './pages/agents';
import { AiPage, NetworkPage } from './pages/aiNetwork';
import { AnalyticsPage, IntelligencePage } from './pages/analyticsIntel';
import { AuditLogsPage } from './pages/auditLogs';
import { B2bPage, SaasPage } from './pages/b2bSaas';
import { BlogCategoriesPage, BlogPostsPage } from './pages/blog';
import { BulkImportProductsPage } from './pages/BulkImportProductsPage';
import {
  AllProductsPage,
  AttributesPage,
  BrandsPage,
  BulkImportBrandPage,
  BulkExportProductsPage,
  CategoryDiscountPage,
  CategoryPage,
  ColorsPage,
  DigitalProductsPage,
  InHouseProductsPage,
  SellerDigitalPage,
  SellerPhysicalPage,
} from './pages/catalog';
import { BannersPage } from './pages/cms';
import { CreateProductPage } from './pages/CreateProductPage';
import { CustomerDetailsPage } from './pages/customerDetails';
import {
  ClassifiedPackagesPage,
  ClassifiedProductsPage,
  CustomerListPage,
} from './pages/customers';
import { DashboardPage } from './pages/DashboardPage';
import { ErpPage, PosPage } from './pages/erpPos';
import { FestivalThemePage } from './pages/festivalTheme';
import { HomepageContentPage } from './pages/homepageContent';
import { HomepageLayoutPage } from './pages/homepageLayout';
import { LoginPage } from './pages/LoginPage';
import { LogisticsPage } from './pages/logistics';
import {
  CustomAlertPage,
  CouponsPage,
  DynamicPopupPage,
  EmailsNewslettersPage,
  FlashDealsPage,
  SellerOffersPage,
  SubscribersPage,
} from './pages/marketing';
import { AddNotePage, NoteListPage } from './pages/notes';
import { NotificationDashboardPage } from './pages/NotificationDashboardPage';
import {
  AllOrdersPage,
  InhouseOrdersPage,
  PickupOrdersPage,
  ReturnsPage,
  SellerOrdersPage,
  UnpaidOrdersPage,
} from './pages/orders';
import { OrderDetailsPage } from './pages/orders/OrderDetailsPage';
import {
  CrmPage,
  NotFoundPage,
  NotificationsPage,
  PayoutsPage,
  ProductApprovalsPage,
  WalletPage,
} from './pages/pages';
import { GlobalizationPage, PlatformPage, ScalePage } from './pages/platformScale';
import { RbacPage } from './pages/rbac';
import {
  CommissionHistoryReportPage,
  EarningReportPage,
  InHouseSaleReportPage,
  ProductsStockReportPage,
  ProductsWishlistReportPage,
  SellerSaleReportPage,
  UserSearchesReportPage,
  WalletRechargeReportPage,
} from './pages/reports';
import { AllSellersPage, SellerRatingsPage } from './pages/sellers';
import { SettingsPage } from './pages/settings';
import {
  FeaturesActivationPage,
  LanguagesPage,
  CurrencyPage,
  VatTaxPage,
  SmtpSettingsPage,
  PaymentMethodsPage,
  OrderConfigurationPage,
  WalletRedemptionPage,
  ShippingPage,
  StaffsPage,
  ApiSettingsPage,
} from './pages/setupConfigurations';
import {
  SponsoredActiveCampaignsPage,
  SponsoredAnalyticsPage,
  SponsoredCampaignFormPage,
  SponsoredDashboardPage,
  SponsoredDraftCampaignsPage,
  SponsoredExpiredCampaignsPage,
  SponsoredPlacementsPage,
  SponsoredScheduledCampaignsPage,
} from './pages/sponsored';
import { ContactsPage, ProductSupportPage, SupportPage } from './pages/support';
import { SupportMonitoringConsole } from './pages/supportMonitoring';
import { UploadedFilesPage } from './pages/uploadedFiles';
import {
  HomepageSettingsPage,
  AuthenticationLayoutPage,
  HeaderPage,
  FooterPage,
  PagesPage,
} from './pages/websiteSetup';
import { useAuthStore } from './stores/authStore';

/** Redirects legacy /sales/order/:id links to the canonical /orders/:id route. */
function SalesOrderRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/orders/${id ?? ''}`} replace />;
}

export function App() {
  const bootstrap = useAuthStore((s) => s.bootstrap);
  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  return (
    <QueryClientProvider client={queryClient}>
      <Toaster position="top-center" />
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route element={<AdminShell />}>
            <Route index element={<DashboardPage />} />

            <Route path="products" element={<ProductApprovalsPage />} />
            <Route path="products/new" element={<CreateProductPage />} />
            <Route path="products/all" element={<AllProductsPage />} />
            <Route path="products/in-house" element={<InHouseProductsPage />} />
            <Route path="products/digital" element={<DigitalProductsPage />} />
            <Route path="products/seller/physical" element={<SellerPhysicalPage />} />
            <Route path="products/seller/digital" element={<SellerDigitalPage />} />
            <Route path="products/bulk-import" element={<BulkImportProductsPage />} />
            <Route path="products/bulk-export" element={<BulkExportProductsPage />} />
            <Route path="products/category" element={<CategoryPage />} />
            <Route path="products/category-discount" element={<CategoryDiscountPage />} />
            <Route path="products/brand/all" element={<BrandsPage />} />
            <Route path="products/brand/bulk-import" element={<BulkImportBrandPage />} />
            <Route path="products/attribute" element={<AttributesPage />} />
            <Route path="products/colors" element={<ColorsPage />} />
            <Route path="notes" element={<NoteListPage />} />
            <Route path="notes/new" element={<AddNotePage />} />
            <Route path="orders/all" element={<AllOrdersPage />} />
            <Route path="orders/inhouse" element={<InhouseOrdersPage />} />
            <Route path="orders/seller" element={<SellerOrdersPage />} />
            <Route path="orders/pickup" element={<PickupOrdersPage />} />
            <Route path="orders/unpaid" element={<UnpaidOrdersPage />} />
            <Route path="orders/returns" element={<ReturnsPage />} />
            <Route path="orders/:id" element={<OrderDetailsPage />} />
            {/* Legacy /sales/* redirects — stale links now point to /orders/* */}
            <Route path="sales/all" element={<Navigate to="/orders/all" replace />} />
            <Route path="sales/order/:id" element={<SalesOrderRedirect />} />
            <Route path="customers" element={<CustomerListPage />} />
            <Route path="customers/:id" element={<CustomerDetailsPage />} />
            <Route path="customers/classified-products" element={<ClassifiedProductsPage />} />
            <Route path="customers/classified-packages" element={<ClassifiedPackagesPage />} />
            <Route path="sellers" element={<AllSellersPage />} />
            <Route path="sellers/ratings" element={<SellerRatingsPage />} />
            {/* /vendors redirects to /sellers — KYC is now the Compliance tab */}
            <Route path="vendors" element={<Navigate to="/sellers" replace />} />
            <Route path="agents" element={<AllAgentsPage />} />
            <Route path="agents/new" element={<AgentRegistrationPage />} />
            <Route path="agents/partners" element={<CommercePartnersPage />} />
            <Route path="agents/payout-time" element={<AgentPayoutTimePage />} />
            <Route path="uploaded-files" element={<UploadedFilesPage />} />
            <Route path="reports/earnings" element={<EarningReportPage />} />
            <Route path="reports/in-house-sales" element={<InHouseSaleReportPage />} />
            <Route path="reports/seller-sales" element={<SellerSaleReportPage />} />
            <Route path="reports/stock" element={<ProductsStockReportPage />} />
            <Route path="reports/wishlist" element={<ProductsWishlistReportPage />} />
            <Route path="reports/searches" element={<UserSearchesReportPage />} />
            <Route path="reports/commissions" element={<CommissionHistoryReportPage />} />
            <Route path="reports/wallet-recharges" element={<WalletRechargeReportPage />} />
            <Route path="blog" element={<BlogPostsPage />} />
            <Route path="blog/categories" element={<BlogCategoriesPage />} />
            <Route path="marketing/banners" element={<BannersPage />} />
            <Route path="marketing/flash-deals" element={<FlashDealsPage />} />
            <Route path="marketing/seller-offers" element={<SellerOffersPage />} />
            <Route path="marketing/coupons" element={<CouponsPage />} />
            <Route path="marketing/dynamic-popup" element={<DynamicPopupPage />} />
            <Route path="marketing/custom-alert" element={<CustomAlertPage />} />
            <Route path="marketing/emails" element={<EmailsNewslettersPage />} />
            <Route
              path="marketing/notification"
              element={<Navigate to="/notifications" replace />}
            />
            <Route path="marketing/subscribers" element={<SubscribersPage />} />
            <Route path="sponsored" element={<SponsoredDashboardPage />} />
            <Route path="sponsored/active" element={<SponsoredActiveCampaignsPage />} />
            <Route path="sponsored/draft" element={<SponsoredDraftCampaignsPage />} />
            <Route path="sponsored/scheduled" element={<SponsoredScheduledCampaignsPage />} />
            <Route path="sponsored/expired" element={<SponsoredExpiredCampaignsPage />} />
            <Route path="sponsored/new" element={<SponsoredCampaignFormPage />} />
            <Route path="sponsored/edit/:id" element={<SponsoredCampaignFormPage />} />
            <Route path="sponsored/analytics" element={<SponsoredAnalyticsPage />} />
            <Route path="sponsored/placements" element={<SponsoredPlacementsPage />} />
            <Route path="support/product" element={<ProductSupportPage />} />
            <Route path="support/contacts" element={<ContactsPage />} />
            <Route path="support/monitoring" element={<SupportMonitoringConsole />} />

            <Route path="payouts" element={<PayoutsPage />} />
            <Route path="wallet" element={<WalletPage />} />
            <Route path="crm" element={<CrmPage />} />
            <Route path="notifications" element={<NotificationsPage />} />
            <Route path="notifications/delivery" element={<NotificationDashboardPage />} />
            <Route path="support" element={<SupportPage />} />
            <Route path="erp" element={<ErpPage />} />
            <Route path="pos" element={<PosPage />} />
            <Route path="b2b" element={<B2bPage />} />
            <Route path="saas" element={<SaasPage />} />
            <Route path="analytics" element={<AnalyticsPage />} />
            <Route path="intelligence" element={<IntelligencePage />} />
            <Route path="ai" element={<AiPage />} />
            <Route path="network" element={<NetworkPage />} />
            <Route path="platform" element={<PlatformPage />} />
            <Route path="scale" element={<ScalePage />} />
            <Route path="globalization" element={<GlobalizationPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="account" element={<AccountPage />} />
            <Route path="website/homepage-settings" element={<HomepageSettingsPage />} />
            <Route path="website/homepage-content" element={<HomepageContentPage />} />
            <Route path="website/homepage-layout" element={<HomepageLayoutPage />} />
            <Route path="website/festival-theme" element={<FestivalThemePage />} />
            <Route
              path="website/authentication-layout-settings"
              element={<AuthenticationLayoutPage />}
            />
            <Route path="website/header" element={<HeaderPage />} />
            <Route path="website/footer" element={<FooterPage />} />
            <Route path="website/pages" element={<PagesPage />} />
            <Route path="setup/staffs" element={<StaffsPage />} />
            <Route path="setup/features" element={<FeaturesActivationPage />} />
            <Route path="setup/languages" element={<LanguagesPage />} />
            <Route path="setup/currency" element={<CurrencyPage />} />
            <Route path="setup/tax" element={<VatTaxPage />} />
            <Route path="setup/smtp" element={<SmtpSettingsPage />} />
            <Route path="setup/api-settings" element={<ApiSettingsPage />} />
            <Route path="setup/payment-methods" element={<PaymentMethodsPage />} />
            <Route path="setup/orders" element={<OrderConfigurationPage />} />
            <Route path="setup/wallet" element={<WalletRedemptionPage />} />
            <Route path="setup/shipping" element={<ShippingPage />} />
            <Route path="setup/rbac" element={<RbacPage />} />
            <Route path="logistics" element={<LogisticsPage />} />
            <Route path="setup/audit-logs" element={<AuditLogsPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
